/**
 * DelegacionARCABanner — banner contextual en la ficha del cliente.
 *
 * Aparece cuando `clientes.delegacion_eventanilla !== 'activa'` y el usuario tiene
 * permiso para ver el casillero. Ofrece al contador 3 acciones:
 *  - Ver instrucciones detalladas (dialog wizard).
 *  - Copiar mensaje listo para enviar al cliente por email.
 *  - Copiar mensaje listo para enviar al cliente por WhatsApp.
 *
 * Con e-Ventanilla `activa`, muestra solo una fila con «Delegar más servicios», que
 * abre la auto-delegación para los demás servicios. Sin `autoDelegarAfip` no muestra nada.
 *
 * El CUIT al que el cliente tiene que delegar sale del certificado de ARCA del estudio
 * (`useRepresentanteAfip`), no de una constante: después de un cambio de certificado, un
 * CUIT fijo manda al cliente a delegar a un representante con el que el estudio ya no
 * consulta. Mientras no se puede leer, las instrucciones y los mensajes quedan deshabilitados.
 *
 * El estado se actualiza automáticamente desde el handler `afip-casillero` cuando
 * VeConsumerWS responde error 105 (delegación faltante) o success (delegación activa).
 */

import { useState } from 'react'
import { AlertCircle, MessageSquare, Mail, Eye, Check, CheckCircle2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { usePermissions } from '@/hooks/usePermissions'
import { useRepresentanteAfip } from '@/hooks/useAfipDelegacion'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { formatearCUIT } from '@/lib/utils'
import { AutoDelegarAfipDialog } from './forms/AutoDelegarAfipDialog'

/** A quién delega el cliente y con qué nombre firma el estudio los mensajes. */
interface Representante {
  /** CUIT formateado con guiones. */
  cuit: string
  /** Nombre de la persona del CUIT, para que el cliente la reconozca en ARCA. */
  nombre: string | null
  firma: string
}

const cuitConNombre = ({ cuit, nombre }: Representante) => (nombre ? `${cuit} (a nombre de ${nombre})` : cuit)

export type EstadoDelegacion = 'pendiente' | 'activa' | 'error_105' | 'revocada'

interface DelegacionARCABannerProps {
  estado: EstadoDelegacion
  cuitCliente: string
  nombreCliente: string
  clienteId: string
  organizacionId: string
}

const ESTADO_CONFIG: Record<
  Exclude<EstadoDelegacion, 'activa'>,
  { titulo: string; descripcion: string; tone: 'warn' | 'error' }
> = {
  pendiente: {
    titulo: 'Casillero ARCA — autorización pendiente',
    descripcion:
      'Para que el estudio pueda consultar el casillero electrónico de este cliente, primero tiene que autorizarlo desde el portal AFIP. Es un trámite único de 5 minutos con clave fiscal nivel 3.',
    tone: 'warn',
  },
  error_105: {
    titulo: 'Casillero ARCA — autorización faltante o revocada',
    descripcion:
      'AFIP rechazó la última consulta porque el cliente no tiene delegado el servicio "Consulta y lectura de Comunicaciones" al estudio. Puede que nunca lo haya autorizado o que lo haya revocado.',
    tone: 'error',
  },
  revocada: {
    titulo: 'Casillero ARCA — autorización revocada',
    descripcion:
      'El cliente revocó manualmente la autorización al estudio. Para volver a consultar el casillero, hay que pedirle que la vuelva a otorgar.',
    tone: 'error',
  },
}

function buildEmailTemplate(
  nombreCliente: string,
  cuitCliente: string,
  representante: Representante,
): { asunto: string; cuerpo: string } {
  return {
    asunto: 'Autorización para consultar tu Casillero Electrónico ARCA',
    cuerpo: `Hola ${nombreCliente},

Para que desde el estudio podamos consultar el Casillero Electrónico de ARCA en tu nombre y avisarte cuando recibas notificaciones, necesitamos que nos autorices desde el portal AFIP. Es un trámite único de 5 minutos.

Pasos:

1. Entrá a https://auth.afip.gob.ar con tu CUIT y clave fiscal nivel 3.
2. Buscá el servicio "Administrador de Relaciones de Clave Fiscal".
3. Hacé click en "Nueva Relación".
4. En el formulario:
   • Servicio → click en BUSCAR → AFIP → WebServices → "Consulta y lectura de Comunicaciones"
   • Representante → ingresá nuestro CUIT: ${cuitConNombre(representante)}
5. Confirmá con tu clave fiscal.

Cualquier duda nos avisás.

Saludos,
${representante.firma}

— Cliente: ${nombreCliente} (CUIT ${cuitCliente})`,
  }
}

function buildWhatsAppMessage(nombreCliente: string, representante: Representante): string {
  return `Hola ${nombreCliente}! Para que podamos consultar tu Casillero Electrónico ARCA y avisarte cuando recibas notificaciones, necesitamos que nos autorices en AFIP. Es un trámite único de 5 minutos.

📋 *Pasos*:
1. Entrá a https://auth.afip.gob.ar con tu CUIT y clave fiscal.
2. Servicio: *Administrador de Relaciones de Clave Fiscal*
3. *Nueva Relación*
4. Servicio → AFIP → WebServices → *Consulta y lectura de Comunicaciones*
5. Representante: nuestro CUIT *${cuitConNombre(representante)}*
6. Confirmá con tu clave fiscal.

Cualquier duda avisame!`
}

export function DelegacionARCABanner({
  estado,
  cuitCliente,
  nombreCliente,
  clienteId,
  organizacionId,
}: DelegacionARCABannerProps) {
  const [wizardOpen, setWizardOpen] = useState(false)
  const [autoDelegarOpen, setAutoDelegarOpen] = useState(false)
  const [copiado, setCopiado] = useState<'email' | 'whatsapp' | null>(null)
  const { puede } = usePermissions()
  const representanteQuery = useRepresentanteAfip(estado !== 'activa')
  const { data: configuracion } = useConfiguracionEstudio()

  const representante: Representante | null = representanteQuery.data
    ? {
        cuit: formatearCUIT(representanteQuery.data),
        nombre: configuracion?.responsable_nombre || null,
        firma: configuracion?.nombre || 'El estudio',
      }
    : null

  const autoDelegarDialog = puede.autoDelegarAfip && (
    <AutoDelegarAfipDialog
      open={autoDelegarOpen}
      onOpenChange={setAutoDelegarOpen}
      clienteId={clienteId}
      organizacionId={organizacionId}
      cuitCliente={cuitCliente}
      nombreCliente={nombreCliente}
      delegacionEventanilla={estado}
    />
  )

  if (estado === 'activa') {
    if (!puede.autoDelegarAfip) return null
    return (
      <>
        <div className="rounded-lg border p-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            e-Ventanilla delegada al estudio
          </p>
          <Button size="sm" variant="outline" onClick={() => setAutoDelegarOpen(true)}>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Delegar más servicios
          </Button>
        </div>
        {autoDelegarDialog}
      </>
    )
  }

  const config = ESTADO_CONFIG[estado]

  const handleCopyEmail = async () => {
    if (!representante) return
    const { asunto, cuerpo } = buildEmailTemplate(nombreCliente, cuitCliente, representante)
    const fullText = `Asunto: ${asunto}\n\n${cuerpo}`
    try {
      await navigator.clipboard.writeText(fullText)
      setCopiado('email')
      toast.success('Email copiado al portapapeles')
      setTimeout(() => setCopiado(null), 2000)
    } catch {
      toast.error('No se pudo copiar al portapapeles')
    }
  }

  const handleCopyWhatsApp = async () => {
    if (!representante) return
    try {
      await navigator.clipboard.writeText(buildWhatsAppMessage(nombreCliente, representante))
      setCopiado('whatsapp')
      toast.success('Mensaje WhatsApp copiado al portapapeles')
      setTimeout(() => setCopiado(null), 2000)
    } catch {
      toast.error('No se pudo copiar al portapapeles')
    }
  }

  const tone = config.tone
  const containerClass =
    tone === 'error'
      ? 'border-destructive/40 bg-destructive/5'
      : 'border-amber-300/60 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-950/20'
  const iconClass = tone === 'error' ? 'text-destructive' : 'text-amber-600'

  return (
    <>
      <div className={`rounded-lg border p-4 space-y-3 ${containerClass}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className={`h-5 w-5 shrink-0 mt-0.5 ${iconClass}`} />
          <div className="space-y-1">
            <p className="font-semibold text-sm">{config.titulo}</p>
            <p className="text-sm text-muted-foreground">{config.descripcion}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pl-8">
          {puede.autoDelegarAfip && (
            <Button size="sm" variant="default" onClick={() => setAutoDelegarOpen(true)}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Auto-delegar desde sistema
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setWizardOpen(true)} disabled={!representante}>
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            Ver instrucciones
          </Button>
          <Button size="sm" variant="outline" onClick={handleCopyEmail} disabled={!representante}>
            {copiado === 'email' ? (
              <Check className="h-3.5 w-3.5 mr-1.5" />
            ) : (
              <Mail className="h-3.5 w-3.5 mr-1.5" />
            )}
            Copiar email
          </Button>
          <Button size="sm" variant="outline" onClick={handleCopyWhatsApp} disabled={!representante}>
            {copiado === 'whatsapp' ? (
              <Check className="h-3.5 w-3.5 mr-1.5" />
            ) : (
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
            )}
            Copiar para WhatsApp
          </Button>
        </div>
        {!representante && (
          <p className="text-xs text-muted-foreground pl-8">
            {representanteQuery.isError
              ? 'No se pudo leer el CUIT del estudio. Las instrucciones y los mensajes se habilitan cuando se pueda leer.'
              : 'Leyendo el CUIT del estudio…'}
          </p>
        )}
      </div>

      {autoDelegarDialog}

      <Dialog open={wizardOpen && !!representante} onOpenChange={setWizardOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cómo autorizar al estudio en AFIP</DialogTitle>
            <DialogDescription>
              Pasos que el cliente {nombreCliente} (CUIT {cuitCliente}) tiene que hacer una sola
              vez para que podamos consultar su Casillero Electrónico ARCA.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 text-sm">
            <Step
              numero={1}
              titulo="Entrar al portal AFIP"
              descripcion="Ingresar a https://auth.afip.gob.ar con CUIT y clave fiscal nivel 3."
            />
            <Step
              numero={2}
              titulo='Abrir "Administrador de Relaciones de Clave Fiscal"'
              descripcion='Buscarlo desde la barra de servicios. Si no aparece, primero hay que adherirlo desde "Adherir Servicio".'
            />
            <Step
              numero={3}
              titulo='Click en "Nueva Relación"'
              descripcion="El botón azul del medio en el panel principal."
            />
            <Step
              numero={4}
              titulo="Buscar y seleccionar el servicio"
              descripcion={
                <>
                  Click en <strong>BUSCAR</strong> al lado del campo "Servicio". Después:
                  <br />
                  <strong>AFIP</strong> → <strong>Servicios WebServices</strong> →{' '}
                  <strong>"Consulta y lectura de Comunicaciones"</strong>
                </>
              }
            />
            <Step
              numero={5}
              titulo="Ingresar el CUIT del estudio como Representante"
              descripcion={
                <>
                  Click en <strong>BUSCAR</strong> al lado de "Representante" e ingresar:
                  <span className="block mt-2 font-mono text-base bg-muted px-3 py-2 rounded">
                    {representante?.cuit}
                  </span>
                  {representante?.nombre && (
                    <span className="block mt-1">Figura a nombre de {representante.nombre}.</span>
                  )}
                </>
              }
            />
            <Step
              numero={6}
              titulo="Confirmar con clave fiscal"
              descripcion='Click en "CONFIRMAR" y volver a ingresar la clave fiscal nivel 3 cuando AFIP la pida. El sistema muestra un Formulario F. 3283/E con el comprobante.'
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWizardOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Step({
  numero,
  titulo,
  descripcion,
}: {
  numero: number
  titulo: string
  descripcion: React.ReactNode
}) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
        {numero}
      </div>
      <div className="space-y-1 flex-1 pt-0.5">
        <p className="font-medium">{titulo}</p>
        <div className="text-muted-foreground text-sm">{descripcion}</div>
      </div>
    </div>
  )
}
